import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { getUserPreferences } from 'api/Preferences';

import { buildTargetUserParams } from 'lib/helpers/targetUserParams';

export const userPreferencesKey = ({
  dependencies = [],
  targetUserId = null,
  targetPracticeId = null
} = {}) => [
  'preferences',
  ...dependencies,
  ...(targetUserId ? [targetUserId, targetPracticeId ?? null] : [])
];

/**
 * Reads one named user preference.
 *
 * `targetUserId` reads a colleague's value instead of the signed-in user's and
 * scopes the cache entry to that member, so an admin moving between members
 * never sees another member's value.
 *
 * @param {object} args
 * @param {object} args.params
 * @param {Array} [args.dependencies]
 * @param {object} [args.options]
 * @param {number|string|null} [args.targetUserId]
 */
export const useUserPreferences = ({
  params,
  dependencies = [],
  options,
  targetUserId = null,
  targetPracticeId = null
}) => {
  const navigate = useNavigate();
  const scopedParams = { ...params, ...buildTargetUserParams(targetUserId, targetPracticeId) };

  return useQuery(
    userPreferencesKey({ dependencies, targetUserId, targetPracticeId }),
    () => getUserPreferences(navigate, scopedParams),
    {
      refetchOnMount: !!targetUserId,
      refetchOnWindowFocus: false,
      staleTime: targetUserId ? 0 : Infinity,
      cacheTime: Infinity,
      ...options
    }
  );
};
